import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
const defaultPreferences = {
    cushion_v1: 'Cushion V1',
    cushion_v2: 'Cushion V2',
    cushion_v3: 'Cushion V3',
    cushion_v4: 'Cushion V4',
    divisor_d1: 'Divisor D1',
};
const ColumnPreferencesContext = createContext(undefined);
export const useColumnPreferences = () => {
    const context = useContext(ColumnPreferencesContext);
    if (!context) {
        throw new Error('useColumnPreferences must be used within ColumnPreferencesProvider');
    }
    return context;
};
export const ColumnPreferencesProvider = ({ children }) => {
    const { isAuthenticated, isLoading: authLoading } = useAuth();
    const [preferences, setPreferences] = useState(defaultPreferences);
    const [isLoading, setIsLoading] = useState(true);
    const refreshPreferences = async () => {
        if (!isAuthenticated || authLoading) {
            setPreferences(defaultPreferences);
            setIsLoading(false);
            return;
        }
        try {
            setIsLoading(true);
            const response = await fetch('/api/user/column-preferences', {
                credentials: 'include',
            });
            if (response.ok) {
                const data = await response.json();
                setPreferences(data.preferences || defaultPreferences);
            }
            else {
                setPreferences(defaultPreferences);
            }
        }
        catch (error) {
            console.error('Error fetching column preferences:', error);
            setPreferences(defaultPreferences);
        }
        finally {
            setIsLoading(false);
        }
    };
    const updatePreferences = async (newPreferences) => {
        const updatedPreferences = { ...preferences, ...newPreferences };
        try {
            const response = await fetch('/api/user/column-preferences', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({ preferences: updatedPreferences }),
            });
            if (response.ok) {
                setPreferences(updatedPreferences);
            }
            else {
                throw new Error('Failed to save preferences');
            }
        }
        catch (error) {
            console.error('Error updating column preferences:', error);
            throw error;
        }
    };
    useEffect(() => {
        if (isAuthenticated && !authLoading) {
            refreshPreferences();
        }
        else if (!authLoading) {
            setPreferences(defaultPreferences);
            setIsLoading(false);
        }
    }, [isAuthenticated, authLoading]);
    return (_jsx(ColumnPreferencesContext.Provider, { value: {
            preferences,
            isLoading,
            updatePreferences,
            refreshPreferences,
        }, children: children }));
};
